export enum SubscriptionEventType {
    SubscriptionCreated = 'SUBSCRIPTION_CREATED',
    SubscriptionUpdated = 'SUBSCRIPTION_UPDATED',
    SubscriptionCancelledTemporary = 'SUBSCRIPTION_CANCELLED_TEMPORARY',
    SubscriptionReactivated = 'SUBSCRIPTION_REACTIVATED',
    SubscriptionCancelledPermanent = 'SUBSCRIPTION_CANCELLED_PERMANENT',
    SubscriptionFulfillmentSucceeded = 'SUBSCRIPTION_FULFILLMENT_SUCCEEDED',
    SubscriptionFulfillmentFailed = 'SUBSCRIPTION_FULFILLMENT_FAILED',
    SubscriptionNextDateRescheduled = 'SUBSCRIPTION_NEXT_DATE_RESCHEDULED',
}

export enum SubscriptionStatus {
    Active = 'ACTIVE',
    Cancelled = 'CANCELLED',
    CancelledTemporary = 'CANCELLED_TEMPORARY',
    CancelledPermanent = 'CANCELLED_PERMANENT',
}

export enum SubscriptionChannel {
    Web = 'web',
    App = 'app',
    Aos = 'aos',
    Csr = 'csr',
}

export enum SubscriptionInitiationType {
    Regular = 'regular',
    PdpAdd = 'pdp_add',
    UpcomingAdd = 'upcoming_add',
}

export enum SubscriptionShippingMethod {
    Sth = 'STH',
    Bopis = 'BOPIS',
    Sdd = 'SDD',
}

export enum SubscriptionPaymentType {
    Cc = 'CC',
    Paypal = 'PAYPAL',
}

/**
 * Core fields of a `main.subscriptions` row at the moment the event was
 * committed. `customerId` is serialized as a string because AlloyDB stores it
 * as BIGINT and JSON `number` silently truncates values above 2^53.
 */
export interface SubscriptionCore {
    id: string;
    customerId: string;
    sourceReferenceId: string;
    subscriptionType: string;
    site: string;
    channel: SubscriptionChannel;
    initiationType: SubscriptionInitiationType;
    status: SubscriptionStatus;
    cancelInfoId: string | null;
    sku: string;
    quantity: number;
    frequencyDays: number;
    fulfillmentMethod: SubscriptionShippingMethod;
    addressId: string | null;
    storeId: string | null;
    paymentInstrumentId: string;
    paymentType: SubscriptionPaymentType;
    prescriptionId: string | null;
    authorizationId: string | null;
    petId: string | null;
    offerId: string | null;
    createdAt: string;
    updatedAt: string;
    version: number;
}

export interface SubscriptionDerivedAttributes {
    nextFulfillmentDate: string | null;
    lastSuccessfulFulfillmentDate?: string | null;
    lastSuccessfulFulfillmentId?: string | null;
    successfulFulfillmentsCount?: number;
    failedFulfillmentsCount?: number;
    lastReactivationDate?: string | null;
    createdAt?: string;
    updatedAt?: string;
}

export interface SubscriptionSnapshot extends SubscriptionCore {
    derived: SubscriptionDerivedAttributes;
}

export interface SubscriptionEvent {
    schemaVersion: 1;
    eventId: string;
    eventType: SubscriptionEventType;
    occurredAt: string;
    subscription: SubscriptionSnapshot;
}

export const SUBSCRIPTION_UPDATE_CHANGEABLE_FIELDS = ['offerId', 'sku', 'quantity'] as const;

export const SUBSCRIPTION_UPDATED_REPLAN_FIELDS = ['paymentInstrumentId', 'paymentType', 'addressId'] as const;

export const SUBSCRIPTION_CONSOLIDATION_KEY_FIELDS = [
    'customerId',
    'nextFulfillmentDate',
    'paymentInstrumentId',
    'paymentType',
    'addressId',
] as const;

export type SubscriptionUpdateChangeableField = (typeof SUBSCRIPTION_UPDATE_CHANGEABLE_FIELDS)[number];
export type SubscriptionUpdatedReplanField = (typeof SUBSCRIPTION_UPDATED_REPLAN_FIELDS)[number];
export type SubscriptionConsolidationKeyField = (typeof SUBSCRIPTION_CONSOLIDATION_KEY_FIELDS)[number];

export interface SubscriptionUpdatedEvent extends SubscriptionEvent {
    eventType: SubscriptionEventType.SubscriptionUpdated;
    changedFields: string[];
}
